import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Camera, Loader2, ArrowRight, ChefHat } from "lucide-react";
import { getTimeline, getCooks } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function timeAgo(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export default function Timeline() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getTimeline(), getCooks().catch(() => ({ cooks: [] }))])
      .then(([tl, ck]) => setData({ ...tl, cooks: ck.cooks || [] }))
      .catch(() => setData({ comparisons: [], longest_resident: null, cooks: [] }))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="app-shell min-h-[100dvh] px-6 pt-[calc(env(safe-area-inset-top)+1.5rem)] pb-[calc(env(safe-area-inset-bottom)+3rem)]" data-testid="timeline-screen">
      <div className="flex items-center justify-between mb-8">
        <button data-testid="timeline-back-btn" onClick={() => navigate("/")} className="w-9 h-9 rounded-full glass flex items-center justify-center">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
        </button>
        <span className="text-xs uppercase tracking-[0.28em] font-semibold text-[#5c5c5c]">The case files</span>
        <div className="w-9" />
      </div>

      {loading ? (
        <div className="flex justify-center pt-20">
          <Loader2 className="w-6 h-6 spin-slow text-[#5c5c5c]" />
        </div>
      ) : data.comparisons.length === 0 && (data.cooks || []).length === 0 ? (
        <div className="pt-20 text-center flex flex-col items-center gap-5">
          <p className="font-serif text-3xl leading-tight">No investigations yet.</p>
          <p className="text-sm text-[#5c5c5c] max-w-[18rem]">Every great case starts with a photo of a slightly disappointing fridge.</p>
          <button data-testid="timeline-start-btn" onClick={() => navigate("/capture?role=before")} className="btn-ink rounded-sm px-6 py-4 flex items-center gap-2">
            <Camera className="w-4 h-4" strokeWidth={1.5} /> Open a case
          </button>
        </div>
      ) : (
        <>
          {data.longest_resident && (
            <motion.div
              className="hairline bg-[#1a1a1a] text-[#f4f1ea] rounded-sm p-6 mb-10"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="longest-resident-card"
            >
              <p className="text-xs uppercase tracking-[0.28em] font-semibold text-[#d4a373]">Longest resident</p>
              <p className="font-serif text-4xl font-black mt-2 leading-none">{data.longest_resident.name}</p>
              <p className="text-sm opacity-70 mt-2">
                Spotted lingering across {data.longest_resident.count} investigation{data.longest_resident.count > 1 ? "s" : ""}. Pays no rent.
              </p>
            </motion.div>
          )}

          <div className="flex flex-col gap-4">
            {data.comparisons.map((c, i) => (
              <motion.button
                key={c.id}
                data-testid={`timeline-item-${c.id}`}
                onClick={() => navigate(`/reveal/${c.id}`)}
                className="glass rounded-sm p-3 flex items-center gap-4 text-left hover:bg-white/50 transition-colors"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
              >
                <div className="flex gap-1 shrink-0">
                  <img src={`${BACKEND_URL}${c.before_url}`} alt="before" loading="lazy" className="w-12 h-16 object-cover rounded-sm border border-[#e2dfd8] bg-[#e9e5db]" />
                  <img src={`${BACKEND_URL}${c.after_url}`} alt="after" loading="lazy" className="w-12 h-16 object-cover rounded-sm border border-[#e2dfd8] bg-[#e9e5db]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#5c5c5c]">{timeAgo(c.created_at)}</p>
                  <p className="font-serif text-lg leading-tight mt-0.5 truncate">{c.result?.summary_stat || "A quiet day"}</p>
                  <p className="text-sm text-[#5c5c5c] mt-0.5 line-clamp-1">{c.result?.summary_copy || c.result?.vibe_copy}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-[#5c5c5c] shrink-0" strokeWidth={1.5} />
              </motion.button>
            ))}
          </div>

          {(data.cooks || []).length > 0 && (
            <>
              <p className="text-xs uppercase tracking-[0.28em] font-semibold text-[#4a5c62] mt-10 mb-4">In the kitchen</p>
              <div className="flex flex-col gap-4">
                {data.cooks.map((k, i) => (
                  <motion.button
                    key={k.id}
                    data-testid={`timeline-cook-${k.id}`}
                    onClick={() => navigate(`/recipes/${k.id}`)}
                    className="glass rounded-sm p-3 flex items-center gap-4 text-left hover:bg-white/50 transition-colors"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                  >
                    <img src={`${BACKEND_URL}${k.image_url}`} alt="fridge" loading="lazy" className="w-14 h-16 object-cover rounded-sm border border-[#e2dfd8] bg-[#e9e5db] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-[#5c5c5c] flex items-center gap-1">
                        <ChefHat className="w-3 h-3" strokeWidth={1.5} /> {timeAgo(k.created_at)}
                      </p>
                      <p className="font-serif text-lg leading-tight mt-0.5 truncate">
                        {(k.result?.recipes || []).slice(0, 2).map((r) => r.title).join(" · ") || "A cook"}
                      </p>
                      <p className="text-sm text-[#5c5c5c] mt-0.5 line-clamp-1">{k.result?.intro_copy}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-[#5c5c5c] shrink-0" strokeWidth={1.5} />
                  </motion.button>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
